import { useQuery } from "@apollo/client";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { Table } from "../../common/table/Table";
import { REPORT_EVENTS } from "./dashboardReports.queries";
import { EVENT_LABELS, EVENT_TYPES, EventType } from "./eventTypes";
import { PickedMember } from "./MemberPicker";
import { formatCount, formatDateTime } from "./reportFormat";
import { rangeVariables, ReportFilterState } from "./ReportFilters";
import { ResultView } from "./reportParams";
import { BarList, MemberLink, ReportError, Section } from "./reportParts";

/** Who or what else an event involved: the other side of a referral, or the place. */
function involved(event: any): React.ReactNode {
  if (event.type === "REFERRAL" && event.relatedMember) {
    return (
      <>
        referred <MemberLink member={event.relatedMember} />
      </>
    );
  }
  if (event.type === "REGISTRATION" && event.relatedMember) {
    return (
      <>
        referred by <MemberLink member={event.relatedMember} />
      </>
    );
  }
  if (event.business) {
    return (
      <Box>
        <Box>{event.business.name}</Box>
        {event.business.address && (
          <Typography variant="caption" color="text.secondary">
            {event.business.address}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
}

const COLUMNS = [
  { label: "When", key: "occurredAt", skipSorting: true, minWidth: "170px", rowRender: (event: any) => formatDateTime(event.occurredAt) },
  { label: "What", key: "type", skipSorting: true, minWidth: "170px", rowRender: (event: any) => EVENT_LABELS[event.type as EventType]?.singular ?? event.type },
  { label: "Member", key: "member", skipSorting: true, rowRender: (event: any) => <MemberLink member={event.member} /> },
  { label: "Who or where", key: "involved", skipSorting: true, minWidth: "260px", rowRender: involved },
  { label: "Detail", key: "detail", skipSorting: true, rowRender: (event: any) => event.detail ?? "" },
];

/**
 * Every registration, app open, referral, recommendation, level change and
 * community action in the range, newest first, with both sides of a referral
 * and the place a recommendation was for.
 */
export const EventsTab: React.FC<{
  filters: ReportFilterState;
  types: EventType[];
  member: PickedMember | null;
  view: ResultView;
}> = ({ filters, types, member, view }) => {
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const range = rangeVariables(filters);

  // A new question starts on its first page. Derived rather than reset in an
  // effect, which would first fetch the old page number for the new question.
  const question = JSON.stringify([range, types, member?.id, rowsPerPage]);
  const [paging, setPaging] = useState({ question, page: 0 });
  const page = paging.question === question ? paging.page : 0;
  const setPage = (next: number) => setPaging({ question, page: next });

  const { data, loading, error } = useQuery(REPORT_EVENTS, {
    variables: {
      range,
      filter: { types: types.length ? types : null, userId: member?.id ?? null },
      page: { take: rowsPerPage, skip: page * rowsPerPage },
    },
    fetchPolicy: "cache-and-network",
  });

  const report = data?.reportEvents;
  const counts = new Map<string, number>((report?.counts ?? []).map((count: any) => [count.type, count.count]));

  return (
    <Stack spacing={2}>
      <ReportError error={error} />
      {loading && <LinearProgress />}
      {view === "chart" ? (
        <Section title="How many of each" note="Every kind in the range, whichever kinds were picked. The table view lists each event.">
          <BarList
            title="Events by kind"
            rows={EVENT_TYPES.map((type) => ({ key: type, label: EVENT_LABELS[type].plural, value: counts.get(type) ?? 0 })).sort(
              (a, b) => b.value - a.value,
            )}
          />
        </Section>
      ) : (
        <Section title="Events" note="A referral shows who referred and who was referred; a recommendation shows the place.">
          {report && (
            <Typography variant="body2" color="text.secondary">
              {formatCount(report.total)} events
            </Typography>
          )}
          <Table
            loading={loading}
            rows={report?.events ?? []}
            columns={COLUMNS}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            total={report?.total ?? 0}
          />
        </Section>
      )}
    </Stack>
  );
};
